import { CandidateActionButtons } from "@components/campaigns/CandidateActionButtons";
import { CandidateDetailPanel } from "@components/campaigns/CandidateDetailPanel";
import { StageBadge } from "@components/campaigns/stages/StageBadge";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { OpenFullProfileLink } from "@components/candidates/OpenFullProfileLink";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { type ThresholdFailure, ThresholdFlag } from "@components/shared/ThresholdFlag";
import { ALL_CAMPAIGN_CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { currentSelectionStep, type SelectionStep, stepDisplayLabel } from "@lib/selectionSteps";
import { CANDIDATE_STAGES, STAGE_LABELS } from "@lib/types";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Search } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "urql";

interface PipelineCandidate {
  id: string;
  stage: string;
  createdAt: string;
  candidate: {
    id: string;
    referenceNumber?: number | null;
    fullName: string;
    currentRoleTitle?: string;
    currentCompany?: string;
    location?: string;
  };
  addedBy: { id: string; name: string };
  candidateAssessment?: {
    id: string;
    currentVersion: {
      id: string;
      thresholdPassing: boolean | null;
      thresholdPassingWithOverride: boolean | null;
      thresholdOverriddenAt: string | null;
      thresholdOverrideReason: string | null;
      thresholdOverriddenBy: { id: string; name: string } | null;
      thresholdFailures: ThresholdFailure[];
    } | null;
  } | null;
  candidateSelectionSteps: SelectionStep[];
}

// Sub-label shown under the Interview stage badge: where the candidate
// currently sits within their defined selection steps.
function interviewStepSubLabel(steps: SelectionStep[]): string {
  if (steps.length === 0) return "Awaiting setup";
  const current = currentSelectionStep(steps);
  return current ? stepDisplayLabel(current) : "All steps complete";
}

type SortField = "name" | "stage" | "createdAt";
type SortDirection = "asc" | "desc";

export function CandidatePipelineTable({ campaignId }: { campaignId: string }) {
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: ALL_CAMPAIGN_CANDIDATES_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const [expanded, setExpanded] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const candidates: PipelineCandidate[] = data?.campaign?.campaignCandidates ?? [];

  const filtered = useMemo(() => {
    let result = candidates;

    if (stageFilter) {
      result = result.filter((cc) => cc.stage === stageFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((cc) => {
        const c = cc.candidate;
        return (
          c.fullName.toLowerCase().includes(q) ||
          (c.currentRoleTitle?.toLowerCase().includes(q) ?? false) ||
          (c.currentCompany?.toLowerCase().includes(q) ?? false)
        );
      });
    }

    return result;
  }, [candidates, search, stageFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") {
        cmp = a.candidate.fullName.localeCompare(b.candidate.fullName);
      } else if (sortField === "stage") {
        const stageOrder = CANDIDATE_STAGES as readonly string[];
        cmp = stageOrder.indexOf(a.stage) - stageOrder.indexOf(b.stage);
      } else if (sortField === "createdAt") {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortDir === "asc" ? (
      <ArrowUp size={12} className="inline ml-1" />
    ) : (
      <ArrowDown size={12} className="inline ml-1" />
    );
  }

  if (fetching && candidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading candidates...</p>;
  }

  return (
    <div className="rounded-lg bg-card shadow-sm ring-1 ring-ring">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-text-secondary hover:bg-card-hover"
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        All Candidates
        <span className="ml-1 text-text-faint">({candidates.length})</span>
      </button>

      {expanded && (
        <div>
          <div className="flex items-center gap-3 border-t border-border px-4 py-3">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-faint"
              />
              <input
                type="text"
                placeholder="Search candidates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input-border bg-card pl-10 pr-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="rounded-md border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">All Stages</option>
              {CANDIDATE_STAGES.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          {error ? (
            <div className="border-t border-border p-4">
              <QueryErrorState
                error={error}
                what="the candidate list"
                onRetry={refresh}
                retrying={fetching}
              />
            </div>
          ) : sorted.length === 0 ? (
            <div className="border-t border-border px-4 py-8 text-center">
              <p className="text-sm text-text-muted">
                {candidates.length === 0 ? "No candidates yet" : "No candidates match your filters"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto border-t border-border">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-card-alt">
                  <tr>
                    <th className="w-8 px-2 py-3" />
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
                      onClick={() => toggleSort("name")}
                    >
                      Name
                      <SortIcon field="name" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Current Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Threshold
                    </th>
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
                      onClick={() => toggleSort("stage")}
                    >
                      Stage
                      <SortIcon field="stage" />
                    </th>
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
                      onClick={() => toggleSort("createdAt")}
                    >
                      Date Added
                      <SortIcon field="createdAt" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sorted.map((cc) => {
                    const isRowExpanded = expandedId === cc.id;
                    return (
                      <Fragment key={cc.id}>
                        <tr className="hover:bg-card-hover">
                          <td className="w-8 px-2 py-3">
                            <button
                              type="button"
                              onClick={() => setExpandedId(isRowExpanded ? null : cc.id)}
                              className="rounded p-0.5 text-text-faint hover:bg-card-hover"
                              aria-label={isRowExpanded ? "Collapse details" : "Expand details"}
                            >
                              {isRowExpanded ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-text-primary">
                            {typeof cc.candidate.referenceNumber === "number" && (
                              <>
                                <CandidateReferenceBadge
                                  referenceNumber={cc.candidate.referenceNumber}
                                />{" "}
                              </>
                            )}
                            <Link
                              to={`/campaigns/${campaignId}/candidates/${cc.id}`}
                              className="text-accent hover:underline"
                            >
                              {cc.candidate.fullName}
                            </Link>
                            <div className="mt-1">
                              <OpenFullProfileLink candidateId={cc.candidate.id} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-text-secondary">
                            {cc.candidate.currentRoleTitle || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-secondary">
                            {cc.candidate.currentCompany || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-secondary">
                            {cc.candidate.location || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {cc.candidateAssessment?.currentVersion ? (
                              <ThresholdFlag
                                passing={cc.candidateAssessment.currentVersion.thresholdPassing}
                                overridden={
                                  !!cc.candidateAssessment.currentVersion.thresholdOverriddenAt
                                }
                                overrideReason={
                                  cc.candidateAssessment.currentVersion.thresholdOverrideReason
                                }
                                failures={cc.candidateAssessment.currentVersion.thresholdFailures}
                                size="sm"
                              />
                            ) : (
                              <span className="text-xs text-text-faint">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <StageBadge stage={cc.stage} />
                            {cc.stage === "interview" && (
                              <div
                                className="mt-1 text-xs text-text-muted"
                                data-testid={`pipeline-step-${cc.id}`}
                              >
                                {interviewStepSubLabel(cc.candidateSelectionSteps)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-secondary">
                            {new Date(cc.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CandidateActionButtons
                              campaignId={campaignId}
                              campaignCandidateId={cc.id}
                              candidateId={cc.candidate.id}
                              candidateName={cc.candidate.fullName}
                              candidateRole={cc.candidate.currentRoleTitle}
                              candidateCompany={cc.candidate.currentCompany}
                            />
                          </td>
                        </tr>
                        {isRowExpanded && (
                          <tr>
                            <td colSpan={9} className="bg-card-alt px-4 py-0">
                              <CandidateDetailPanel
                                campaignCandidateId={cc.id}
                                candidateId={cc.candidate.id}
                                candidateName={cc.candidate.fullName}
                                candidateRole={cc.candidate.currentRoleTitle}
                                candidateCompany={cc.candidate.currentCompany}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
